import * as Yup from 'yup';
import { Component } from 'react';
import { DocumentType, Locale, Media } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { Formik, FormikProps } from 'formik';
import { PoolButton, PoolModal, PoolTypography, ScrollToFieldError } from '@synergy/components';
import { Theme } from '@mui/material';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { connect } from 'react-redux';
import { removeUploadedFile, uploadFile } from '@synergy/data-access';

import { AppState } from '../../../../store/models/app-state';
import { EditOffer } from './model/edit-offer';
import { Offer } from '../../../../common/models/offer';
import { OfferCompleteStatus } from '../../../../common/models/enums/offer-complete-status';
import { OfferEditForm } from './offer-edit-form';
import { OfferEditModalHeader } from './components/offer-edit-modal-header';
import { OfferStatus } from '../../../../common/models/enums/offer-status';
import { ShopPrice } from '../../../../common/api/offer-api';
import { Supplier } from '../../../../common/models/supplier';
import { TermsAndConditions } from '../../../../common/models/terms-and-conditions';
import { User } from '../../../../common/models/user';
import { fetchBonusConditions, resetBonusConditions } from '../../../../common/actions/account-actions';
import { fetchShopPrices } from '../../../../common/actions/offer-actions';

const styles = (theme: Theme) =>
    createStyles({
        creatorInformation: {
            flex: 1,
            justifySelf: 'left',
            marginLeft: theme.spacing(2),
        },
    });

interface OfferEditModalOwnProps {
    isVisible: boolean;
    currency: string;
    locale: Locale;
    suppliers?: Supplier[];
    offer?: Offer;
    /** Set when a new offer is entered for this delivery address. */
    accountPurchaseId?: number;
    readonly: boolean;
    onClose: () => void;
    onSave?: (offer: Partial<Offer>, newFiles: File[], mediaToRemove: Media[], supplierAccount?: Supplier) => void;
}

interface OfferEditModalReduxProps extends WithStyles<typeof styles> {
    mediaBaseUrl: string;
    user: User;
    termsAndConditions?: TermsAndConditions;
    fetchBonusConditions: (accountId: number) => void;
    resetBonusConditions: () => void;
    fetchShopPrices: (accountPurchaseId: number, supplierAccountId: number) => Promise<ShopPrice[]>;
    uploadFile: (docType: DocumentType, docId: number, file: File) => Promise<boolean>;
    removeUploadedFile: (docType: DocumentType, docId: number, media: Media) => Promise<void>;
}

type OfferEditModalProps = OfferEditModalReduxProps & OfferEditModalOwnProps;

interface OfferEditModalState {
    supplier?: Supplier;
}

class OfferEditModalBase extends Component<OfferEditModalProps, OfferEditModalState> {
    private formikInstance: FormikProps<EditOffer> | null = null;
    private termsAndConditionCache: { [key: number]: TermsAndConditions } = [];

    public state: OfferEditModalState = {
        supplier: this.props.suppliers?.[0],
    };

    private handleSupplierChanged = (supplier: Supplier) => {
        this.setState({ supplier });
        this.updateTerms(supplier);
        this.fillShopPrices(supplier);
    };

    private handleSubmit = (values: EditOffer) => {
        if (this.props.onSave && this.props.offer) {
            // Create offer
            const offer: Partial<Offer> = {
                ...values,
                status: OfferStatus.Pending,
                offerSubmittedBy: values.offerSubmittedBy || this.props.user.contact,
            };

            // Set complete status
            const isComplete = values.offerItems.some(item => !item.isOffered);
            const isDivergent = values.offerItems.some(item => item.isAlternative);
            offer.completeStatus = isComplete
                ? isDivergent
                    ? OfferCompleteStatus.Divergent
                    : OfferCompleteStatus.Complete
                : OfferCompleteStatus.Incomplete;

            // Get upload/remove attachments
            const existingMedia = this.props.offer?.media ?? [];
            const newFiles = values.media.reduce(
                (acc: File[], item: Media) => (item.file !== undefined ? [...acc, item.file] : acc),
                []
            );
            const mediaToRemove = existingMedia.filter(
                existingItem => !values.media.some(media => media.id === existingItem.id)
            );

            // Return partial offer object
            this.props.onSave(offer, newFiles, mediaToRemove, this.state.supplier);
        }
    };

    private handleSave = () => {
        // false positive, should be fixed in future eslint releases
        // eslint-disable-next-line no-unused-expressions
        this.formikInstance?.submitForm();
    };

    private handleReset = () => {
        // eslint-disable-next-line no-unused-expressions
        this.formikInstance?.resetForm();
        this.updateTerms();
    };

    private getSupplierId = (supplier?: Supplier) =>
        (this.props.offer?.id ?? -1) >= 0
            ? this.props.offer?.supplierAccount.id
            : (supplier ?? this.state.supplier)?.id;

    // Set terms and conditions from cache or request them
    private updateTerms = (supplier?: Supplier) => {
        const supplierId = this.getSupplierId(supplier);
        if (supplierId === undefined) {
            return;
        }

        // Set form-values from cache if possible else request data
        const termsAndConditions = this.termsAndConditionCache[supplierId];
        if (termsAndConditions) {
            // false positive, should be fixed in future eslint releases
            // eslint-disable-next-line no-unused-expressions
            this.formikInstance?.setFieldValue('supplierBonus', termsAndConditions.bonusConditions || '');
            // eslint-disable-next-line no-unused-expressions
            this.formikInstance?.setFieldValue('termsOfDeliveryContent', termsAndConditions.termsOfDelivery || '');
            // eslint-disable-next-line no-unused-expressions
            this.formikInstance?.setFieldValue('paymentTerm', termsAndConditions.paymentTerm);
        } else {
            this.props.resetBonusConditions();
            this.props.fetchBonusConditions(supplierId);
        }
    };

    /**
     * A new offer starts from what the shop charges for the supplier's own
     * articles, as a purchase order does; prices already typed stay
     * (key-user brief A3.2).
     */
    private fillShopPrices = async (supplier?: Supplier) => {
        const { accountPurchaseId } = this.props;

        if (!accountPurchaseId || !supplier) {
            return;
        }

        const prices = await this.props.fetchShopPrices(accountPurchaseId, supplier.id);
        const formik = this.formikInstance;

        if (!formik || this.state.supplier?.id !== supplier.id) {
            return;
        }

        formik.values.offerItems.forEach((item, index) => {
            const shopPrice = prices.find(price => price.purchaseItemId === item.purchaseItem?.id);

            if (shopPrice && (item.price === undefined || item.price === null)) {
                formik.setFieldValue(`offerItems.${index}.price`, shopPrice.price);
            }
        });
    };

    public componentDidMount() {
        if (this.props.accountPurchaseId) {
            this.fillShopPrices(this.state.supplier);
        }

        if (
            this.props.offer?.supplierAccount?.id &&
            this.props.offer?.termsOfDeliveryContent != null &&
            this.props.offer?.paymentTerm != null &&
            this.props.offer?.supplierBonus != null
        ) {
            this.termsAndConditionCache[this.props.offer.supplierAccount.id] = {
                bonusConditions: this.props.offer.supplierBonus,
                termsOfDelivery: this.props.offer.termsOfDeliveryContent,
                paymentTerm: this.props.offer.paymentTerm,
            };
        } else {
            this.updateTerms(this.state.supplier);
        }
    }

    public UNSAFE_componentWillReceiveProps(nextProps: OfferEditModalProps) {
        if (this.formikInstance && this.props.termsAndConditions === undefined && nextProps.termsAndConditions) {
            // Store in cache & update fields
            const supplierId = this.getSupplierId();
            if (supplierId === undefined) {
                return;
            }
            // Update cached termsAndConditions with new ones if applicable
            const cachedTerms = this.termsAndConditionCache[supplierId];
            const updatedTermsAndConditions: TermsAndConditions = {
                bonusConditions:
                    cachedTerms?.bonusConditions ??
                    nextProps.offer?.supplierBonus ??
                    nextProps.termsAndConditions.bonusConditions,
                termsOfDelivery:
                    cachedTerms?.termsOfDelivery ??
                    nextProps.offer?.termsOfDeliveryContent ??
                    nextProps.termsAndConditions.termsOfDelivery,
                paymentTerm:
                    cachedTerms?.paymentTerm ??
                    nextProps.offer?.paymentTerm ??
                    nextProps.termsAndConditions.paymentTerm,
            };
            this.termsAndConditionCache[supplierId] = updatedTermsAndConditions;
            this.updateTerms();
        }
    }

    public render() {
        const { isVisible, onClose, suppliers, offer, classes, readonly } = this.props;
        const { supplier } = this.state;

        if (!isVisible || !offer) {
            // Don't render if not visible or no data
            return null;
        }

        const actionChildren = [
            offer.status !== OfferStatus.Created && (
                <PoolTypography key="1" className={classes.creatorInformation}>
                    {offer.offerSubmittedBy && (
                        <FormattedMessage
                            id={'app.inquiries.detail.offerEdit.createdBy'}
                            values={{ value: offer.offerSubmittedBy.fullName }}
                        />
                    )}
                </PoolTypography>
            ),
            !readonly && (
                <PoolButton key="2" variant="text" styleType="firstLevel" onClick={this.handleReset}>
                    <FormattedMessage id="app.common.reset" />
                </PoolButton>
            ),
            <PoolButton key="3" styleType="firstLevel" onClick={readonly ? this.props.onClose : this.handleSave}>
                {readonly ? <FormattedMessage id="app.common.close" /> : <FormattedMessage id="app.common.save" />}
            </PoolButton>,
        ];

        const isNew = !!this.props.offer && (this.props.offer.id === undefined || this.props.offer.id < 0);
        const schema = Yup.object().shape({
            deliveryPrice: Yup.number()
                .required('app.error.required')
                .typeError('app.error.required')
                .min(0, 'app.error.positive'),
            offerItems: Yup.array().of(
                Yup.object().shape({
                    isOffered: Yup.boolean(),
                    isAlternative: Yup.boolean(),
                    price: Yup.number().when('isOffered', {
                        is: true,
                        then: Yup.number().required('app.error.required').typeError('app.error.required'),
                    }),
                    alternativeNote: Yup.string().when(['isOffered', 'isAlternative'], {
                        is: (isOffered: boolean, isAlternative: boolean) => isOffered && isAlternative,
                        then: Yup.string().required('app.error.required'),
                    }),
                })
            ),
            paymentTerm: Yup.object().nullable().required('app.error.required'),
        });

        const initialValues: EditOffer = {
            id: offer.id,
            deliveryPrice: offer.deliveryPrice,
            internalNote: offer.internalNote || '',
            noteFromSupplier: offer.noteFromSupplier || '',
            supplierDeclinedReason: offer.supplierDeclinedReason || '',
            offerItems: [...offer.offerItems],
            media: [...offer.media],
            surcharge: offer.surcharge || 0,
            offerSubmittedBy: offer.offerSubmittedBy,
            supplierBonus: offer.supplierBonus || '',
            termsOfDeliveryContent: offer.termsOfDeliveryContent || '',
            paymentTerm: offer.paymentTerm,
            supplierOfferNumber: offer.supplierOfferNumber || '',
        };

        const modalTitle = (
            <span>
                <FormattedMessage id="app.inquiries.detail.offerEdit" />
                {offer && offer.supplierAccount && <span> - {offer.supplierAccount.name}</span>}
            </span>
        );

        return (
            <PoolModal
                handleClose={onClose}
                isVisible={isVisible}
                actionChildren={actionChildren}
                title={modalTitle}
                width="lg"
            >
                {isNew && suppliers && supplier && (
                    <OfferEditModalHeader
                        suppliers={suppliers}
                        supplierId={supplier && supplier.id}
                        onSupplierChanged={this.handleSupplierChanged}
                    />
                )}
                <Formik
                    innerRef={ref => (this.formikInstance = ref)}
                    onSubmit={this.handleSubmit}
                    validationSchema={schema}
                    initialValues={initialValues}
                >
                    {formikProps => (
                        <>
                            <ScrollToFieldError />
                            <OfferEditForm
                                formikProps={formikProps}
                                currency={this.props.currency}
                                locale={this.props.locale}
                                mediaBaseUrl={this.props.mediaBaseUrl}
                                readonly={readonly}
                            />
                        </>
                    )}
                </Formik>
            </PoolModal>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    mediaBaseUrl: state.settings.mediaBaseUrl,
    user: state.user,
    termsAndConditions: state.accounts.termsAndConditions,
});

export const ConnectedOfferEditModal = connect(mapStateToProps, {
    uploadFile,
    removeUploadedFile,
    resetBonusConditions,
    fetchBonusConditions,
    fetchShopPrices,
})(OfferEditModalBase);

export const OfferEditModal = withStyles(styles)(ConnectedOfferEditModal);
